import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * <pdp-variant-fragments> wraps the PDP content. Listens for variant:update
 * dispatched by <variant-picker>, picks up the parsed response document from
 * event.detail.data.html, and morphs every [data-pdp-fragment] descendant
 * with its counterpart in the new document.
 *
 * Used for blocks that depend on variant state but aren't morphed by
 * variant-picker.js itself (labels, themed picker).
 */
class PdpVariantFragments extends HTMLElement {
  connectedCallback() {
    this.handle = this.handle.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this.handle);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.handle);
  }

  /** @param {CustomEvent} event */
  async handle(event) {
    // Skip Events, die aus einer anderen pdp-variant-fragments-Instanz stammen
    // (z.B. PDP-Original reagiert auf Variant-Wechsel, der im Bottom-Up-Sheet
    // ausgelöst wurde — gleiche Klasse, anderer Scope).
    if (event.target instanceof Element && !this.contains(event.target)) return;

    let newDoc = event.detail?.data?.html;
    if (!newDoc) {
      const variantId = event.detail?.resource?.id;
      if (!variantId) return;
      // Derive the section id from the DOM (rename-proof; this is a rarely-hit
      // fallback — variant-picker.js normally supplies event.detail.data.html).
      const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
      const url = `${location.pathname}?section_id=${sectionId}&variant=${variantId}`;
      const res = await fetch(url);
      const text = await res.text();
      newDoc = new DOMParser().parseFromString(text, 'text/html');
    }

    // Sync the sold-out marker from the freshly rendered section so CSS-driven
    // blocks (.hide-on-sold-out) react to the new variant without being morphed.
    const incoming = newDoc.querySelector('pdp-variant-fragments');
    if (incoming?.hasAttribute('data-variant-sold-out')) {
      this.setAttribute('data-variant-sold-out', incoming.getAttribute('data-variant-sold-out'));
    }

    for (const target of this.querySelectorAll('[data-pdp-fragment]')) {
      const name = target.getAttribute('data-pdp-fragment');
      const source = newDoc.querySelector(`[data-pdp-fragment="${name}"]`);
      // Clone: morph MOVES nodes out of its source tree, and event.detail.data.html
      // is shared by reference across all variant:update listeners — morphing
      // against the original would cannibalize the doc for later consumers.
      if (source) morph(target, source.cloneNode(true));
    }
  }
}

if (!customElements.get('pdp-variant-fragments')) {
  customElements.define('pdp-variant-fragments', PdpVariantFragments);
}
